import * as React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";

import KImage from './components/KImage';
import Config from './components/ClientConfig';
import Client from './components/Client';
import swal from 'sweetalert';
import noImage from './images/noImage.png';

import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TranslateIcon from '@mui/icons-material/Translate';

import Visibility from './Visibility';

let LANGS_CACHE = null;
let LANGS_BY_ID = null;
let LANGS_LOADING = null;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#273442",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const MemoKImage = React.memo(function MemoKImage({
  edit,
  src,
  image,       // <- fallback for src
  alt,
  imageText,   // <- fallback for alt
  saveBack,
  width = 128,
  height = 128,
}) {
  const srcStr = src !== undefined ? src : image;
  const altStr = alt !== undefined ? alt : imageText;
  const imgObj = { data: srcStr || noImage, width, height }; // build inside memo
  return (
    <KImage
      edit={edit}
      image={imgObj}
      imageText={altStr}
      saveBack={saveBack}
    />
  );
});

class ClassTranslationsPanel extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      translations: [],
      loading: false,
      isSavingTr: false,
      trEdit: {},                 // { [iref]: boolean } row-level edit toggles
      languages: [],
      langById: {},
      languagesReady: false,
    };
    this.loadLanguages = this.loadLanguages.bind(this);
    this.loadTranslations = this.loadTranslations.bind(this);
    this.addBlankTranslation = this.addBlankTranslation.bind(this);
    this.onTrFieldChange = this.onTrFieldChange.bind(this);
    this.saveTranslation = this.saveTranslation.bind(this);
    this.toggleTrEdit = this.toggleTrEdit.bind(this);
  }

  componentDidMount(){
    // lazy-load when mounted
    this.loadLanguages().then(()=> this.loadTranslations());
  }

  toRelative = (u) => (!u ? '' : String(u).replace(/^https?:\/\/[^/]+/i, ''));
  joinUrl = (base, path) => {
    const b = (base || '').endsWith('/') ? base : (base + '/');
    const p = (path || '').startsWith('/') ? path.slice(1) : path;
    return b + p;
  };

  async loadLanguages() {
    if (LANGS_CACHE && LANGS_BY_ID) {
      this.setState({ languages: LANGS_CACHE, langById: LANGS_BY_ID, languagesReady: true });
      return;
    }
    if (LANGS_LOADING) {
      await LANGS_LOADING;
      this.setState({ languages: LANGS_CACHE || [], langById: LANGS_BY_ID || {}, languagesReady: true });
      return;
    }
    LANGS_LOADING = (async () => {
      const base = this.toRelative(this.props.apiLanguages || Config.API_URL_LANGUAGE || '/api/1.0/languages/');
      const data = (this.props.client || new Client('none')).getData(base, {});
      const raw = Array.isArray(data?.Languages) ? data.Languages : [];
      const list = raw
        .map(x => ({ iref: String(x.iref), languageName: String(x.languageName || '').trim() }))
        .filter(x => x.iref && x.languageName);
      const map = {}; list.forEach(l => { map[l.iref] = l; });
      LANGS_CACHE = list; LANGS_BY_ID = map;
    })();
    try { await LANGS_LOADING; } finally { LANGS_LOADING = null; }
    this.setState({ languages: LANGS_CACHE, langById: LANGS_BY_ID, languagesReady: true });
  }

  async loadTranslations() {
    const classId = this.props.classId;
    if (!classId) return;
    this.setState({ loading: true });
    try {
      const base = this.toRelative(this.props.apiClasses || Config.API_URL_PRODUCT_CLASS || '/api/1.0/product_classes/');
      const url  = this.joinUrl(base, `${classId}/translations/`);
      const data = (this.props.client || new Client('none')).getData(url, {});
      const rows = Array.isArray(data?.translations) ? data.translations : [];
      const normalized = rows.map(r => {
        const langId = (r.language && typeof r.language === 'object')
          ? String(r.language.iref)
          : (r.language != null ? String(r.language) : null);
        return { ...r, language: langId };
      });
      this.setState({ translations: normalized, loading: false });
    } catch(e){
      this.setState({ loading: false });
      swal('Error', e.message || String(e), 'error');
    }
  }

  onTrFieldChange(iref, field, value) {
    this.setState(s => ({
      translations: (s.translations || []).map(r => String(r.iref) === String(iref) ? { ...r, [field]: value } : r)
    }));
  }

  hasOpenDraft = () => (this.state.translations || []).some(t => Number(t.iref) < 0);
  canEditTrRow = (t) => !!this.props.allowEdit && (this.state.trEdit?.[t.iref] || t.iref < 0);
  toggleTrEdit(iref, force) {
    this.setState(s => ({ trEdit: { ...s.trEdit, [iref]: typeof force === 'boolean' ? force : !s.trEdit?.[iref] } }));
  }

  addBlankTranslation() {
    if (!this.props.classId) { swal('Error','Save the class first, then add translations.','error'); return; }
    if (this.hasOpenDraft()) {
      swal('Heads up', 'Please finish or cancel the current draft translation first.', 'info');
      return;
    }
    const tmpId = -Date.now();
    const newRow = {
      iref: tmpId,
      productClass: this.props.classId,
      language: null,
      name: '',
      description: '',
      shortName: ''
    };
    this.setState(s => ({
      translations: [newRow, ...(s.translations || [])],
      trEdit: { ...s.trEdit, [tmpId]: true }
    }));
  }

  saveTranslation = async (row) => {
    const classId = this.props.classId;
    if (!classId) { swal('Error','Save the class first, then add translations.','error'); return; }
    this.setState({ isSavingTr: true });

    // client-side duplicate guard
    if (row.language) {
      const dup = (this.state.translations || []).some(r =>
        String(r.language || '') === String(row.language) &&
        String(r.iref) !== String(row.iref)
      );
      if (dup) {
        this.setState({ isSavingTr: false });
        swal('Duplicate', 'A translation for this language already exists for this class.', 'warning');
        return;
      }
    }

    const isRealId = row.iref && Number(row.iref) > 0;
    const payload = {
      ...(isRealId ? { iref: row.iref } : {}),
      language_iref: row.language || null,
      name: (row.name ?? '').trim(),
      description: (row.description ?? '').trim(),
      shortName: (row.shortName ?? '').trim()
    };
    try {
      const base = this.toRelative(this.props.apiClasses || Config.API_URL_PRODUCT_CLASS || '/api/1.0/product_classes/');
      const url  = this.joinUrl(base, `${classId}/translations_save/`);
      const data = (this.props.client || new Client('none')).pushData(url, payload);
      if (data && data.status === 'OK') {
        const saved  = data.row;
        const langId = saved?.language && typeof saved.language === 'object'
          ? String(saved.language.iref) : String(saved.language || '');
        const merged = { ...saved, language: langId || null };
        this.setState(s => {
          const list = [...(s.translations || [])];
          const idx = list.findIndex(x => x.iref === row.iref || x.iref === merged.iref);
          if (idx >= 0) list[idx] = merged; else list.unshift(merged);
          return { translations: list, isSavingTr: false, trEdit: { ...s.trEdit, [merged.iref]: false } };
        });
        swal('Saved','Translation saved.','success',{ buttons:false, timer:900 });
      } else {
        const msg = (data && (data.message || JSON.stringify(data.errors))) || 'Unknown error';
        swal('Save failed', msg, 'error'); this.setState({ isSavingTr: false });
      }
    } catch (e) {
      swal('Save failed', e.message || String(e), 'error');
      this.setState({ isSavingTr: false });
    }
  };

  render(){
    const rows = this.state.translations || [];
    return (
      <Box sx={{ p: 1.0, display:'grid', gridTemplateColumns:'1fr', gap: 1 }}>
        <Box sx={{ display:'flex', gap: 1, alignItems:'center', justifyContent:'space-between' }}>
          <Typography variant="subtitle2">Translations</Typography>
          {this.props.allowEdit && this.props.classId && (
            <Button size="small" variant="outlined" onClick={this.addBlankTranslation} disabled={this.hasOpenDraft() || this.state.loading}>
              Add translation
            </Button>
          )}
        </Box>

        {this.state.loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No translations yet.</Typography>
        ) : (
          rows.map((t) => {
            const isTemp = (t.iref < 0);
            return (
              <Box key={t.iref} sx={{ p: 1, borderRadius: 1, border: '1px solid #e0e0e0', display:'grid', gridTemplateColumns:'240px 1fr', gap: 1 }}>
                {/* Language column */}
                <Box sx={{ display:'grid', gap: .5 }}>
                  {this.canEditTrRow(t) ? (
                    <Autocomplete
                      options={this.state.languages}
                      size="small"
                      disablePortal
                      getOptionLabel={(opt) => opt?.languageName || ''}
                      isOptionEqualToValue={(opt, val) => opt?.iref === val?.iref}
                      value={(this.state.languagesReady && t.language) ? (this.state.langById[t.language] || null) : null}
                      onChange={(_, val) => this.onTrFieldChange(t.iref, 'language', val ? val.iref : null)}
                      renderInput={(params) => <TextField {...params} variant="outlined" label="Language" />}
                      ListboxProps={{ sx: { maxHeight: 200, p: 0 } }}
                      sx={{ minWidth: 240, mt: 1 }}
                      disabled={!this.state.languagesReady}
                    />
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary">Language</Typography>
                      <Typography variant="body2">
                        {!this.state.languagesReady ? '…' : (t.language ? (this.state.langById[t.language]?.languageName || '—') : '—')}
                      </Typography>
                    </>
                  )}
                </Box>

                {/* Fields column */}
                <Box sx={{ display:'grid', gap: 0.5 }}>
                  {this.canEditTrRow(t) ? (
                    <Box
                      sx={{
                        display:'grid',
                        columnGap: 0.5,
                        rowGap: 0.5,
                        gridTemplateColumns: '1fr 160px auto'
                      }}
                    >
                      {/* Name (col 1) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Name" variant="outlined"
                        value={t.name || ''}
                        inputProps={{ maxLength: 100 }}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'name', e.target.value)}
                        sx={{ gridColumn: '1 / span 1' }}
                      />
                      {/* Short name (col 2) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Short name" variant="outlined"
                        value={t.shortName || ''}
                        inputProps={{ maxLength: 16 }}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'shortName', e.target.value)}
                        sx={{ gridColumn: '2 / span 1' }}
                      />
                      {/* Actions (col 3) */}
                      <Box sx={{ gridColumn: '3 / span 1', display:'flex', gap: 1, justifyContent:'flex-end', alignItems:'center', mt: 1 }}>
                        <Button size="small" variant="contained" disabled={this.state.isSavingTr} onClick={()=>this.saveTranslation(t)}>
                          {this.state.isSavingTr ? 'Saving…' : (isTemp ? 'Add' : 'Save')}
                        </Button>
                        <Button size="small" onClick={()=>{
                          if (isTemp) {
                            this.setState(s => ({
                              translations: (s.translations || []).filter(x => x.iref !== t.iref),
                              trEdit: { ...s.trEdit, [t.iref]: false }
                            }));
                          } else {
                            this.toggleTrEdit(t.iref, false);
                          }
                        }}>Cancel</Button>
                      </Box>
                      {/* Description (row 2, spans all 3) */}
                      <TextField
                        size="small" margin="dense" fullWidth
                        label="Description" variant="outlined"
                        multiline rows={2}
                        inputProps={{ maxLength: 1024 }}
                        value={t.description || ''}
                        onChange={(e)=>this.onTrFieldChange(t.iref, 'description', e.target.value)}
                        sx={{ gridColumn: '1 / span 3' }}
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle2">{t.name || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">{t.description || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">Short: {t.shortName || '—'}</Typography>
                      {this.props.allowEdit && (
                        <Box sx={{ display:'flex', justifyContent:'flex-end', mt:.5 }}>
                          <Button size="small" onClick={()=>this.toggleTrEdit(t.iref, true)}>Edit</Button>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    );
  }
}

class DataRow extends React.Component {
  constructor(props) {
    super(props);

    this.state = {   
      client: new Client ( "none" ),   
      edit: false,
      row: {},
      nameValid: false,
      shortNameValid: false,
      priorityValid: false,
      descriptionValid: false,
      image_off: noImage,
      somethingHasChanged: false,
      allowEdit: false
    };

    
    this.handleEditClick = this.handleEditClick.bind(this);
    this.getImage = this.getImage.bind(this);
    this.getImage_off = this.getImage_off.bind(this);
    this.changeValue = this.changeValue.bind(this);
    this.pushData = this.pushData.bind(this);
    //this.classAdded = this.classAdded.bind(this);

  }

  componentDidMount() {
    let row = {};
    if (this.props)
      if (this.props.allowEdit === true)
        this.setState ({allowEdit: true})
    if ( this.props.row ) {
      row = this.props.row;
      this.setState ( { row: row } );
      if ( row.name.length > 0 ) this.setState ( {nameValid: true } );
      if ( row.shortName && row.shortName.length > 0 ) this.setState ( {shortNameValid: true } );
      if ( row.description.length > 0 ) this.setState ( {descriptionValid: true } );
      if ( row.priority.toString().length > 0 ) this.setState ( {priorityValid: true } );
      
    }
    if ( this.props.edit ) this.setState ( { edit: true } );
  }

  componentDidUpdate ( prevProps ) {
    if ( this.props !== prevProps ) {
      if (this.props)
        if (this.props.allowEdit === true)
          this.setState ({allowEdit: true})
      if ( this.props.row ) {
        let row = this.props.row;
        this.setState ( { row: row } );
        if ( row.name.length > 0 ) this.setState ( {nameValid: true } );
        if ( row.shortName && row.shortName.length > 0 ) this.setState ( {shortNameValid: true } );
        if ( row.description.length > 0 ) this.setState ( {descriptionValid: true } );
        if ( row.priority.toString().length > 0 ) this.setState ( {priorityValid: true } );
      }
    }

  }

  handleEditClick ( ) {
    if (!this.state.allowEdit)
      return;
    this.setState({ edit: !this.state.edit });
  }

  getImage ( newImage ) {
    if ( newImage ) {
      if ( this.state.row ) {
        let nRow = this.state.row;
        nRow.image = newImage;
        this.setState ( { row: nRow }, this.setState ( { somethingHasChanged: true } ) );
      }
    }
  }

  getImage_off ( newImage ) {
    if ( newImage ) {
      if ( this.state.row ) {
        let nRow = this.state.row;
        nRow.image_off = newImage;
        this.setState ( { row: nRow }, this.setState ( { somethingHasChanged: true } ) );
      }
    }
  }

  changeValue ( e ) {
    let id = null;
    if ( e.target.id !== null ) id = e.target.id;
    let value = null;
    if  ( e.target.value !== null ) value = e.target.value;
    //if ( id !== null && value !== null && value !== undefined ) {
    if ( id ) {
      let row = {};
      if ( this.state.row ) row = this.state.row;
      switch ( id ) {
        case "name":
          if ( value.length > 0 ) {
            row.name = value;
            this.setState ( { nameValid: true });
          } else {
            row.name = "";
            this.setState ( { nameValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
        case "shortName":
          if ( value.length > 0 ) {
            row.shortName = value;
            this.setState ( { shortNameValid: true });
          } else {
            row.shortName = "";
            this.setState ( { shortNameValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
        case "description":
          if ( value.length > 0 ) {
            row.description = value;
            this.setState ( { descriptionValid: true });
          } else {
            row.description = "";
            this.setState ( { descriptionValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
        case "priority":
          if ( value.length > 0 ) {
            row.priority = value;
            this.setState ( { priorityValid: true });
          } else {
            row.priority = 0;
            this.setState ( { priorityValid: false });
          }
          this.setState ( { somethingHasChanged: true } );
          break;
      }
      this.setState ( { row: row });
    }
  }

  pushData ( e ) {
    if ( this.state.somethingHasChanged && this.state.nameValid && this.state.shortNameValid && this.state.priorityValid && this.state.descriptionValid ) {
      let rdata = this.state.client.pushData ( Config.API_URL_PRODUCT_CLASS_ADD, this.state.row );
      if ( rdata ) {
        if ( rdata.status === "OK" ) {
          let uCategory = this.state.row;
          //uCategory.iref = rdata.iref;
          uCategory = rdata;
          this.setState ( { row: uCategory } );
          this.handleEditClick ( );
          swal ( "Success", "Successfully added/modified category ! (" + rdata.iref + ")", "success", {
            buttons: false,
            timer: 2000,
          })
          .then((value) => {
            //this.props.personUpdated ( );
            this.setState ( {edit: false} );
            this.setState ( { somethingHasChanged: false } );
            this.props.classAdded ();
          });        
        } else {
          if ( rdata.message ) this.setState ( { errorMessage: rdata.message } );
          swal ( "Error", "Add/Update category failed !\n\n" + rdata.message, "error" );
        }
      }
    } else {
      swal ( "Error", "Add/Update category failed ! Some fields are not validated !", "error" );
    }
  }

  render() {
    let row = {};
    let iref = "0";
    let name = "";
    let shortName = ""
    let priority = 0
    let description = "";
    let image = noImage;
    let image_off = noImage;
    let productsCounter = 0;
    if ( this.state.row ) {
      row = this.state.row;
      if ( row.iref ) iref = row.iref;
      if ( row.name ) name = row.name;
      if ( row.shortName ) shortName = row.shortName;
      if ( row.priority ) priority = row.priority;
      if ( row.image ) image = row.image;
      if ( row.image_off ) image_off = row.image_off;
      if ( row.description ) description = row.description;
      if (row.productsCounter) productsCounter = row.productsCounter;
    }
    if ( this.props.dataRow !== null && this.props.dataRow !== undefined ) {
      row = this.props.dataRow;
    }

    let imageName = name + "_on";
    let imageName_off = name + "_off";
    
    let showSave = false;
    if ( this.state.edit === true && this.state.somethingHasChanged === true && this.state.nameValid === true && this.state.shortNameValid === true && this.state.priorityValid === true && this.state.descriptionValid === true )
      showSave = true;

    return (
      <StyledTableRow key={row.iref}>
        <StyledTableCell align="left">
          {iref}
        </StyledTableCell>
        <StyledTableCell component="th" scope="row">
          {this.state.edit ? 
          <TextField
            id="name"
            label="Name"
            variant="outlined"
            value={name || ''}
            onChange={this.changeValue}
            onFocus={event => { event.target.select(); }}
            inputProps={{maxLength:"32"}}
            error={this.state.nameValid === false}
            required={true}
            sx={{minWidth: "100px"}}
          />
          : name }
        </StyledTableCell>

        <StyledTableCell align="left">
          {this.state.edit ? 
            <TextField
              id="shortName"
              label="Short Name"
              variant="outlined"
              value={shortName || ''}
              onChange={this.changeValue}
              onFocus={event => { event.target.select(); }}
              inputProps={{maxLength:"16"}}
              error={this.state.shortNameValid === false}
              required={true}
              sx={{minWidth: "100px"}}
            />
            :shortName}
          </StyledTableCell>
        
        <StyledTableCell align="left">
          {this.state.edit ? 
            <TextField
              id="description"
              label="Description"
              variant="outlined"
              value={description || ''}
              onChange={this.changeValue}
              onFocus={event => { event.target.select(); }}
              inputProps={{maxLength:"64"}}
              error={this.state.descriptionValid === false}
              required={true}
              sx={{minWidth: "200px"}}
            />
            :description}
          </StyledTableCell>

          <StyledTableCell align="left">
            {this.state.edit ? 
              <TextField
                id="priority"
                label="Priority"
                type="number"
                variant="outlined"
                value={priority || 0}
                onChange={this.changeValue}
                onFocus={event => { event.target.select(); }}
                error={this.state.priorityValid === false}
                inputProps={{min:0}}
                required={true}
                sx={{minWidth: "80px"}}
              />
              :priority}
          </StyledTableCell>

        <StyledTableCell align="left">
          <MemoKImage
            edit = {this.state.edit}
            image = {image}
            imageText = {imageName}
            saveBack = {this.getImage}
           />
        </StyledTableCell>
        <StyledTableCell align="left">
          <MemoKImage
              edit = {this.state.edit}
              image = {image_off}
              imageText = {imageName_off}
              saveBack = {this.getImage_off}
           />
        </StyledTableCell>
        <StyledTableCell align="left">
          {productsCounter}
        </StyledTableCell>
        <StyledTableCell align="left">
          <Chip
            icon={<TranslateIcon />}
            label="Translations"
            size="small"
            color={this.props.trOpen ? "primary" : "default"}
            variant={this.props.trOpen ? "filled" : "outlined"}
            onClick={() => this.props.toggleTr(this.state.row.iref)}
            sx={{ fontWeight: 600 }}
          />
          </StyledTableCell>
        <StyledTableCell align="right">
          <IconButton aria-label="settings" onClick={this.handleEditClick}>
            <EditIcon
              sx={{ color: this.state.edit ? "#8f241d" : "#e3e3e5" }}              
            />
        </IconButton>
        {showSave ?
          <IconButton aria-label="settings" onClick={this.pushData}>
            <SaveIcon
              sx={{ color: "#4287f5" }}            
            />
      </IconButton>
        : ""}
        </StyledTableCell>
      </StyledTableRow>
    );
  }
}

class Classes extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      client: new Client ( "none" ),
      productClasses: null,
      edit: false,
      addClass: false,
      trOpenByClass: {},
      //addNewProduct: false,
    };

    this.classAdded = this.classAdded.bind(this);
    this.toggleAdd = this.toggleAdd.bind(this);
    this.getData = this.getData.bind(this);
    this.restricted = new Visibility ("products_classes");
    this.toggleTrForClass = this.toggleTrForClass.bind(this);
  }

  toggleTrForClass(classId, force){
    this.setState(s => ({
      trOpenByClass: { ...s.trOpenByClass, [classId]: (typeof force === 'boolean' ? force : !s.trOpenByClass?.[classId]) }
    }));
  }

  getData ( ) {
    let queryData = {}
    queryData[ "orderBy" ] = "name";
    let productsClasses = this.state.client.getProductsClasses ( queryData );
    this.setClasses ( productsClasses.productClasses );
  }

  componentDidMount() {  
    if (this.restricted.scope.visible)
      this.getData ( );    
  }

  componentWillUnmount() {    
  }

  setClasses ( classes ) {
    let a = { }
    if ( classes !== null && classes !== undefined ) {
      classes.map ( (productClass) => {
        a[ productClass.iref ] = productClass;        
      });
      this.setState ( { productClasses : a });
    }
  }

  classAdded ( ) {
    this.getData ( );
    this.setState ( { addClass: false } );
  }

  toggleAdd ( ) {
    if (!this.restricted.scope.edit)
      return;
    let addButton = this.state.addClass;
    this.setState ( {addClass: !addButton} );
    //this.props.toggleAdd ( !addButton );
  }
  
  render() {
    if (!this.restricted.scope.visible)
      return (<div></div>);
    let productClasses = [];
    if ( this.state.productClasses !== null && this.state.productClasses !== undefined ) {
      productClasses = Object.values ( this.state.productClasses );
    }

    let allData ="";
    if ( productClasses ) {
      allData = productClasses.map((row) => {
        return <DataRow
          key = {row.iref}
          row = {row}
          classAdded={this.classAdded}
          allowEdit={this.restricted.scope.edit}
        />
      });
    }

    return (

      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 80px)' }}>
      <Table sx={{ minWidth: 700 }} stickyHeader aria-label="customized table">
        <TableHead>
          <TableRow>
            <StyledTableCell>
              <IconButton aria-label="Add Location" title="Add Location" onClick={() => this.toggleAdd()}>       
                <Avatar alt="Add Location" sx={{ width: 24, height: 24, color: "#273442" }}>
                  {this.state.addClass ? <FaMinusCircle/> : <FaPlusCircle/>}
                </Avatar>
              </IconButton>
            </StyledTableCell>
            <StyledTableCell>
              Class Name
            </StyledTableCell>
            <StyledTableCell align="left">Short Name</StyledTableCell>
            <StyledTableCell align="left">Description</StyledTableCell>
            <StyledTableCell align="left">Priority</StyledTableCell>
            <StyledTableCell align="left">Image (On)</StyledTableCell>
            <StyledTableCell align="left">Image (Off)</StyledTableCell>
            <StyledTableCell align="left">Products</StyledTableCell>
            <StyledTableCell align="left">Translations</StyledTableCell>
            <StyledTableCell align="right">Action</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {this.state.addClass && this.restricted.scope.edit ? (
            <DataRow
              key={0}
              edit={true}
              classAdded={this.classAdded}
              allowEdit={this.restricted.scope.edit}
            />
          ) : null}
          {/* {allData} */}
          {(this.state.productClasses ? Object.values(this.state.productClasses) : []).map((row) => (
            <React.Fragment key={row.iref}>
              <DataRow
                row={row}
                classAdded={this.classAdded}
                allowEdit={this.restricted.scope.edit}
                toggleTr={this.toggleTrForClass}
                trOpen={!!this.state.trOpenByClass[row.iref]}
              />
              <StyledTableRow>
                <StyledTableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                  <Collapse in={!!this.state.trOpenByClass[row.iref]} timeout="auto" unmountOnExit>
                    <Box sx={{ background: '#fafafa', borderTop: '1px solid #eee', p: 1.5 }}>
                      <ClassTranslationsPanel
                        classId={row.iref}
                        allowEdit={this.restricted.scope.edit}
                        client={this.state.client}
                        apiClasses={Config.API_URL_PRODUCT_CLASS}
                        apiLanguages={Config.API_URL_LANGUAGE}
                      />
                    </Box>
                  </Collapse>
                </StyledTableCell>
              </StyledTableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
      

    );

  }
}

export default Classes;
